import {
  EventEmitter,
  Injectable,
  Optional,
  Output,
} from '@angular/core';
import {
  filter,
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import { get } from 'lodash-es';
import { interval, Observable, Subscription } from 'rxjs';
// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { RestApiService } from '@ui-kit';
import {
  AppNotification,
  appNotificationDefaultOptions,
  AppNotificationOptions,
  mapAlertToNotification,
  uuid,
} from '../component/notification/notification.helpers';
import { UserService } from './user.service';
import { LeanFavourite } from '../../public/favourites/shortlist.helpers';

export interface NotificationEvent {
  type: 'change' | 'new';
  data?: AppNotification[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  @Output() event: EventEmitter<NotificationEvent> = new EventEmitter();

  get nativeNotificationSupport(): boolean {
    return 'Notification' in window;
  }

  get nativeNotificationPermission(): boolean {
    return this.nativeNotificationSupport
      && Notification?.permission === 'granted';
  }

  get alerts(): P.Alert[] {
    return this.alertStore;
  }

  set alerts(alerts: P.Alert[]) {
    const change = !!alerts.length
      && this.alerts.length !== alerts.length;

    const delta = !!this.alerts.length
      && change
      ? [...alerts].splice(0, alerts.length - this.alerts.length)
      : [];

    this.alertStore = alerts;

    if (change) {
      this.event.emit({ type: 'change' });
    }

    if (delta.length) {
      this.event.emit({
        type: 'new',
        data: delta
          .map((alert) => mapAlertToNotification(alert, { dismissible: true, dismissTime: 4000 })),
      });
    }
  }

  public getAlerts(): void {
    this.restApi.getAlerts({ readAt: '', limit: '999' }).pipe(map((page) => page.docs)).subscribe((alerts) => this.alerts = alerts);
  }

  get alertsObservable(): Observable<P.Alert[]> {
    return this.event.pipe(
      filter((e) => e.type === 'change'),
      map(() => this.alerts),
      startWith(this.alerts),
    );
  }

  subscription: Dictionary<Subscription> = {};

  private alertStore: P.Alert[] = [];

  constructor(
    // @Optional() private window: Window,
    private user: UserService,
    private restApi: RestApiService,
  ) {
    this.user.signedInObservable
      .pipe(tap(this.nativeNotificationRequestPermission));
  }

  /**
   *
   */
  public notify(message: string, options: AppNotificationOptions = {}): void {
    const notification: AppNotification = {
      id: uuid(),
      body: message,
      options: { ...appNotificationDefaultOptions, ...options },
    };
    this.event.emit({
      type: 'new', data: [notification],
    });
  }

  public addAlert(alert: P.Alert): void {
    this.event.emit({ type: 'change' });
    this.event.emit({
      type: 'new',
      data: [mapAlertToNotification(alert, { dismissible: true, dismissTime: 4000 })],
    });
  }

  public notifyEnquiryStatus(enquiry: LeanFavourite) {
    const { status, listing } = enquiry;
    const notificationStatuses = ['Accepted', 'Declined'];
    if (!notificationStatuses.includes(status)) {
      return;
    }
    const address = get(listing, 'source_data.displayable_address');
    const isAccepted = status === 'Accepted';
    const title = `${isAccepted ? 'Great' : 'Oops'}! The home ${address ? `on ${address}` : 'you checked'} `
      + `${isAccepted ? 'is eligible.🎉🎉🎉' : 'was auto rejected'}`;

    const alert = <P.Alert>{
      type: isAccepted ? 'AutoApproved' : 'AutoRejected',
      probe: { id: enquiry.id },
      title,
    };
    this.addAlert(alert);
  }

  /**
   *
   */
  private nativeNotificationRequestPermission(signedIn: boolean): void {
    // if user not signed in or notification not supported, do nothing
    if (!signedIn || !('Notification' in window)) {
      return;
    }

    // if has not granted or denied permission
    if (!Notification?.permission || Notification?.permission === 'default') {
      // on permission callback
      const onPermission = (permission: string) => {
        if (permission === 'granted' && !!Notification) {
          const notification = new Notification('Hello there', {
            body: 'Thanks',
            icon: '/assets/icons/icon-128x128.png',
          });
        }
      };

      // ask for permission
      if (Notification?.requestPermission) {
        Notification.requestPermission()
          .then((permission) => onPermission(permission))
          .catch(console.error);
      }
    } else if (Notification?.permission === 'granted') {
      // do nothing
    }
  }
}
